/**
 * The TDDA territory maps, and where a point falls on them (S3.4, bug 5695).
 *
 * A site's TDDA region is a geographic fact, so it is settled geographically: the address picker
 * gives a latitude and longitude, and the region is whichever mapped territory that point falls
 * inside. This replaces matching region names against the words of the address, which could not work
 * — "Auckland" names three franchises and no address distinguishes them, while a Google-formatted
 * address ends in the country name and so matched every region containing it.
 *
 * The maps are the same KML files the Imperans client-office screen reads, so both products place a
 * site in the same territory. Australia and New Zealand have separate files.
 *
 * Everything here degrades quietly. A missing file, a network failure or unparseable XML leaves the
 * caller with no territories, which reads as "could not detect" — the region is then chosen by hand,
 * exactly as it is today.
 */

export type KmlTerritory = {
  /** The Placemark name, verbatim: "Hawke's Bay, NZ". Matched against the mapping from the API. */
  name: string;
  /** Each ring is a closed list of [longitude, latitude] pairs. */
  polygons: [number, number][][];
};

const AU_KML_URL =
  (process.env.REACT_APP_KML_AU_URL || "").trim() ||
  "https://staimperansoccf.blob.core.windows.net/imperansdev/maps/Australia.kml";

const NZ_KML_URL =
  (process.env.REACT_APP_KML_NZ_URL || "").trim() ||
  "https://staimperansoccf.blob.core.windows.net/imperansdev/maps/regions.kml";

export const kmlUrlForCountry = (countryCode: string | null | undefined): string => {
  const code = `${countryCode ?? ""}`.trim().toUpperCase();
  return code === "AU" ? AU_KML_URL : NZ_KML_URL;
};

/**
 * Parses a KML document into territories.
 *
 * Every `<coordinates>` element under a Placemark becomes a ring, and a point inside any ring counts
 * as inside the territory — which is what the Imperans implementation does, so the two agree. It
 * means an inner boundary (a hole) is treated as solid; none of the current TDDA maps uses one.
 *
 * Coordinates are KML's own order, `longitude,latitude[,altitude]`, whitespace-separated. Note that
 * the parser decodes XML entities, so a Placemark written `Hawke&apos;s Bay, NZ` arrives here as
 * `Hawke's Bay, NZ` — which is how the mapping table spells it too.
 */
export const parseKml = (kmlText: string): KmlTerritory[] => {
  let xml: Document;
  try {
    xml = new DOMParser().parseFromString(kmlText, "text/xml");
  } catch {
    return [];
  }

  if (xml.getElementsByTagName("parsererror").length > 0) return [];

  const territories: KmlTerritory[] = [];
  const placemarks = xml.getElementsByTagName("Placemark");

  for (let i = 0; i < placemarks.length; i++) {
    const placemark = placemarks[i];
    const name = (placemark.getElementsByTagName("name")[0]?.textContent || "").trim();
    const coordinateNodes = placemark.getElementsByTagName("coordinates");
    const polygons: [number, number][][] = [];

    for (let j = 0; j < coordinateNodes.length; j++) {
      const ring: [number, number][] = [];
      for (const pair of (coordinateNodes[j].textContent || "").trim().split(/\s+/)) {
        const parts = pair.split(",");
        if (parts.length < 2) continue;
        const lng = parseFloat(parts[0]);
        const lat = parseFloat(parts[1]);
        if (Number.isFinite(lng) && Number.isFinite(lat)) ring.push([lng, lat]);
      }
      // Two points cannot enclose anything, so a degenerate ring is dropped rather than carried
      // around to be tested against.
      if (ring.length >= 3) polygons.push(ring);
    }

    if (name && polygons.length > 0) territories.push({ name, polygons });
  }

  return territories;
};

/**
 * Whether a point is inside a ring, by ray casting: count the ring edges a ray from the point crosses
 * and an odd count means inside.
 *
 * Works on the raw longitude/latitude plane rather than projecting, which is what the Imperans
 * implementation does and is accurate at the scale that matters here — territory borders are tens of
 * kilometres apart, and the question is only which side of one an address sits on. It does mean a
 * territory spanning the 180th meridian would be wrong; none of the TDDA territories does, though New
 * Zealand comes closest.
 */
export const isPointInRing = (
  latitude: number,
  longitude: number,
  ring: [number, number][],
): boolean => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const straddles = yi > latitude !== yj > latitude;
    if (straddles && longitude < ((xj - xi) * (latitude - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
};

/** The first territory containing the point, or null. */
export const territoryAt = (
  latitude: number,
  longitude: number,
  territories: readonly KmlTerritory[],
): KmlTerritory | null => {
  for (const territory of territories) {
    for (const ring of territory.polygons) {
      if (isPointInRing(latitude, longitude, ring)) return territory;
    }
  }
  return null;
};

/**
 * The territories for a country, fetched once per country per page.
 *
 * The New Zealand map is around three megabytes, so re-fetching it for every address a user picks
 * would be conspicuous. The cache holds the promise rather than the result, so two selections in
 * quick succession share one download instead of racing. A failed load is not cached, so the next
 * selection tries again.
 */
const cache = new Map<string, Promise<KmlTerritory[]>>();

export const loadTerritories = (countryCode: string | null | undefined): Promise<KmlTerritory[]> => {
  const url = kmlUrlForCountry(countryCode);
  const cached = cache.get(url);
  if (cached) return cached;

  const pending = fetch(url)
    .then((response) => {
      if (!response.ok) throw new Error(`KML request failed: ${response.status}`);
      return response.text();
    })
    .then(parseKml)
    .catch(() => {
      cache.delete(url);
      return [] as KmlTerritory[];
    });

  cache.set(url, pending);
  return pending;
};

/** Test seam: forget what has been downloaded. */
export const resetTerritoryCache = (): void => cache.clear();
